import {
    CreateSavedSearchModal,
    type BaseSavedSearch,
} from '@finn-no/saved-search-settings-component';
import { i18n } from '@lingui/core';
import { toast } from '@warp-ds/elements/toast';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAppConfig } from '../../AppConfig';
import {
    trackSavedSearchClick,
    trackSavedSearchComplete,
} from '../../tracking/trackSavedSearch';
import { LoginModal } from './LoginModal';
import { useSavedSearchData } from './SavedSearchData';
import { useSubmitSavedSearch } from './SavedSearchQuery';
import { useSavedSearchState } from './SavedSearchState';
import { UpdateSavedSearchModal } from './UpdateSavedSearchModal';
import { useLoginRedirect } from './useLoginRedirect';

type SavedSearchProps = {
    alwaysShow?: boolean;
    children: React.ReactElement | ContentRenderProp;
};

export type ContentRenderProp = (args: {
    isModified: boolean;
    isSaveable: boolean;
    isUnsaved: boolean;
    onClick: () => void;
}) => React.ReactElement | null;

export function SavedSearch(props: SavedSearchProps) {
    const { alwaysShow = false, children } = props;

    const { loginId } = useAppConfig();
    const isLoggedIn = Boolean(loginId);

    const [isOpen, setIsOpen] = React.useState(false);
    const { isLoginRedirect, setIsLoginRedirect, onLogin } = useLoginRedirect();

    const isModalOpen = isOpen || isLoginRedirect;

    const onDismiss = React.useCallback(() => {
        setIsOpen(false);
        setIsLoginRedirect(false);
    }, [setIsLoginRedirect]);

    const {
        isModified,
        isSaveable,
        isUnsaved,
        savedSearchId,
        savedSearchInput,
    } = useSavedSearchState();

    const { isError, savedSearchData } = useSavedSearchData(savedSearchId);

    const { updateSavedSearch } = useSubmitSavedSearch();

    React.useEffect(() => {
        if (isError) {
            toast(
                i18n.t({
                    id: 'saved-search.render.error',
                    message:
                        'An error occurred while fetching your saved search.',
                }),
                { type: 'error' },
            );
        }
    }, [isError]);

    function onFetchError() {
        toast(
            i18n.t({
                id: 'saved-search.render.error',
                message: 'An error occurred while fetching your saved search.',
            }),
            { type: 'error' },
        );
    }

    function onCreate(savedSearch: BaseSavedSearch) {
        updateSavedSearch(savedSearch.id);
        trackSavedSearchComplete(savedSearch.id, true);
    }

    function onUpdate(id?: number) {
        if (id) {
            updateSavedSearch(id);
            trackSavedSearchComplete(id, false);
            toast(
                i18n.t({
                    id: 'saved-search.update.success',
                    message: 'Your search was updated!',
                }),
                { type: 'success' },
            );
        } else {
            // This should not happen
            onUpdateError();
        }
    }

    function onUpdateError() {
        toast(
            i18n.t({
                id: 'saved-search.update.error',
                message:
                    'An error occurred while updating the search. Try again later.',
            }),
            {
                type: 'error',
            },
        );
    }

    function clearSavedSearchId() {
        updateSavedSearch(null);
    }

    function onClick() {
        setIsOpen(true);
        trackSavedSearchClick();
    }

    const showContent = alwaysShow || isModified || isUnsaved;
    if (!showContent) {
        return null;
    }

    const hasRenderProp = typeof children === 'function';
    const content = hasRenderProp
        ? children({ isModified, isSaveable, isUnsaved, onClick })
        : children;

    return (
        <ErrorBoundary fallback={content} onError={onFetchError}>
            {content}
            {isModalOpen && (
                <>
                    {isLoggedIn && savedSearchData && (
                        <UpdateSavedSearchModal
                            isModified={isModified}
                            isOpen={isModalOpen}
                            onDismiss={onDismiss}
                            onError={onUpdateError}
                            onSave={onUpdate}
                            onSaveAsNew={clearSavedSearchId}
                            savedSearchData={savedSearchData}
                            savedSearchInput={savedSearchInput!}
                        />
                    )}
                    {isLoggedIn && isSaveable && !savedSearchId && (
                        <CreateSavedSearchModal
                            closeModal={onDismiss}
                            isOpen={isModalOpen}
                            onCreate={onCreate}
                            search={savedSearchInput!}
                        />
                    )}
                    {!isLoggedIn && (
                        <LoginModal
                            isOpen={isModalOpen}
                            onDismiss={onDismiss}
                            onLogin={onLogin}
                        />
                    )}
                </>
            )}
        </ErrorBoundary>
    );
}
